import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../features/auth/authSlice';
import {
  useSubmitKycMutation,
  useGetInvestorKycQuery,
  useListInvestorKycTemplatesQuery,
  useUploadKycDocumentMutation,
} from '../../features/kyc/kycApi';
import { kycSubmissionSchema, KYC_DOCUMENT_TYPES } from '../../lib/validators/kycSchema';
import { authErrorMessage } from '../../lib/authErrorMessage';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Dropdown from '../../components/ui/Dropdown';
import { Skeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import KycFilesInput from '../../components/kyc/KycFilesInput';
import KycProgressBar from '../../components/kyc/KycProgressBar';
import OtherDocumentsChecklist from '../../components/kyc/OtherDocumentsChecklist';

/**
 * v1.18.0 — unified KYC screen. GET /investor/kyc now gives real identity-
 * submission status plus the new "Other Documents" checklist, replacing the
 * old account_status-inference workaround (no GET existed for identity
 * status before this). That inference is kept as a fallback ONLY — if the
 * GET fails to load or the server doesn't yet send an `identity` field,
 * the screen still degrades to the old behavior rather than going blank.
 *
 * The identity section and the Other Documents section are deliberately
 * two separate Cards with no shared status language — "other" documents
 * never gate account activation and must never read as part of that
 * approval flow (explicit spec rule).
 */
export default function KycForm() {
  const currentUser = useSelector(selectCurrentUser);
  const accountStatus = currentUser?.account_status ?? null;
  const toast = useToast();

  const { data, isLoading: isLoadingKyc } = useGetInvestorKycQuery();
  const { data: templates } = useListInvestorKycTemplatesQuery();
  const [uploadKycDocument, { isLoading: isUploading }] = useUploadKycDocumentMutation();

  const [submitKyc, { isLoading }] = useSubmitKycMutation();
  const [reviewState, setReviewState] = useState(null); // null | 'under_review'

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(kycSubmissionSchema),
    defaultValues: { document_type: '', documents: [] },
  });

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append('document_type', values.document_type);
      values.documents.forEach((file) => formData.append('documents[]', file));

      await submitKyc(formData).unwrap();
      setReviewState('under_review');
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }

      if (err?.status === 409) {
        const message = err?.data?.message || '';
        // The server's own message is the only signal distinguishing
        // "already pending" from "already active" — if it reads like a
        // review-in-progress, reflect that state directly instead of just
        // showing an error.
        if (/pending|review/i.test(message)) {
          setReviewState('under_review');
        } else {
          setError('root', { message: message || 'This account can no longer submit a new KYC request.' });
        }
        return;
      }

      setError('root', {
        message: authErrorMessage(err, {
          tooMany: 'Too many attempts — please wait a moment before submitting again.',
          fallback: 'Submission failed. Please try again.',
        }),
      });
    }
  };

  const handleOtherUpload = async (tag, file) => {
    try {
      const formData = new FormData();
      formData.append('tag', tag);
      formData.append('document', file);
      await uploadKycDocument(formData).unwrap();
      toast.push({ tone: 'success', message: 'Document uploaded.' });
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Upload failed',
        message: authErrorMessage(err, { fallback: 'Could not upload this document.' }),
      });
    }
  };

  // Real identity status from the GET when available; falls back to the
  // old account_status + local-submit-state inference otherwise.
  const identity = data?.identity ?? null;
  const isVerified = identity ? identity.status === 'approved' : accountStatus === 'active';
  const isUnderReview = identity ? identity.status === 'pending' : reviewState === 'under_review';
  const isRejected = identity ? identity.status === 'rejected' : accountStatus === 'rejected' && !isUnderReview;

  const otherDocuments = data?.otherDocuments ?? [];
  const hasOtherDocuments = otherDocuments.length > 0;
  const percent = data?.kycProgress?.percent;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">KYC verification</h1>
        <p className="text-foreground-muted text-sm mt-1">
          Browsing investment plans doesn't require verification — this is only needed before your account goes active.
        </p>
      </div>

      {isLoadingKyc ? (
        <Card>
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-2.5 w-full rounded-full" />
        </Card>
      ) : (
        Number.isFinite(percent) && (
          <Card>
            <KycProgressBar percent={percent} />
          </Card>
        )
      )}

      {isVerified && (
        <Card>
          <div className="flex items-center gap-3">
            <Badge tone="success">Verified</Badge>
            <p className="text-sm text-foreground-muted">Your account is active. There's nothing further to submit here.</p>
          </div>
        </Card>
      )}

      {!isVerified && isUnderReview && (
        <Card>
          <div className="flex items-center gap-3">
            <Badge tone="warning">Under review</Badge>
            <p className="text-sm text-foreground-muted">
              Your documents were submitted and are awaiting review. You'll be notified once a decision is made.
            </p>
          </div>
        </Card>
      )}

      {!isVerified && !isUnderReview && (
        <Card>
          <CardHeader title="Submit your documents" subtitle="One document type, 1–2 files (JPG, PNG, or PDF, up to 5MB each)." />

          {isRejected && (
            <div className="mb-4 rounded-xl border border-danger/30 bg-danger-soft p-3 text-sm text-danger">
              Your last submission was rejected. Please review your documents and submit again below.
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Controller
              name="document_type"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Document type"
                  placeholder="Select a document type"
                  options={KYC_DOCUMENT_TYPES}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.document_type?.message}
                />
              )}
            />

            <Controller
              name="documents"
              control={control}
              render={({ field }) => (
                <KycFilesInput value={field.value} onChange={field.onChange} error={errors.documents?.message} />
              )}
            />

            {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

            <Button type="submit" isLoading={isLoading}>
              Submit for review
            </Button>
          </form>
        </Card>
      )}

      {/* Other Documents — deliberately its own section, never mixed with
          the identity Cards above. These documents don't gate anything. */}
      {hasOtherDocuments && (
        <Card>
          <CardHeader title="Other documents" subtitle="Additional forms Cavan Capital has requested. These don't affect your account status." />
          <OtherDocumentsChecklist
            items={otherDocuments}
            templates={templates}
            onUpload={handleOtherUpload}
            isUploading={isUploading}
          />
        </Card>
      )}
    </div>
  );
}
